import { UI } from './ui';

export namespace User {

  export interface LoginResponse {
    access_token: string;
    expires_in: number;
    refresh_token: string;
    scope: string;
    token_type: string;
  }

  export interface UsersListResponse {
    count: number;
    results: Profile[];
  }

  export interface Profile {
    id: number;
    username: string;
    avatar: string;
    default_avatar?: boolean;
    email: string;
    email_state: EmailStatus;
    has_password?: boolean;
    is_server_owner?: boolean;
    new_email?: string;
    account_verified?: boolean;
    date_joined?: Date;
    role: UserRole;
    social_auth?: UserSocialAuth[];
    online: boolean;
    is_banned: boolean;
    last_action?: Date;
    is_auto_confirm_ip?: boolean;
    is_ability_to_edit_projects?: boolean;
    is_ability_to_delete_projects?: boolean;
    is_auto_review_edits?: boolean;
    is_ability_to_transfer?: boolean;
    is_ability_to_delete_comments?: boolean;
  }

  export interface UserPermissions {
    is_auto_confirm_ip: boolean;
    is_ability_to_edit_projects: boolean;
    is_ability_to_delete_projects: boolean;
    is_auto_review_edits: boolean;
    is_ability_to_transfer: boolean;
    is_ability_to_delete_comments: boolean;
  }

  export interface ManualReplenishment {
    id: number;
    amount: string;
    note: string;
    actor: { id: number; username: string } | null;
    timestamp: Date;
  }

  export interface AdminWallet {
    balance: number;
    manual_replenishments: ManualReplenishment[];
  }

  export const MANUAL_REPLENISH_MAX = 30000;

  export class ReplenishForm extends UI.Form {
    amount: UI.FormField = new UI.FormField();
    note: UI.FormField = new UI.FormField();

    constructor() {
      super();
    }

    get amountNumber(): number {
      return Number(String(this.amount.value).replace(',', '.').trim());
    }

    isValid(): boolean {
      this.amount.error = '';
      this.note.error = '';
      const raw = String(this.amount.value).replace(',', '.').trim();
      if (!raw) { this.amount.error = 'Укажите сумму'; return false; }
      if (!/^\d+(\.\d{1,2})?$/.test(raw)) { this.amount.error = 'Сумма: число, не более 2 знаков после точки'; return false; }
      const amount = Number(raw);
      if (!(amount > 0)) { this.amount.error = 'Сумма должна быть больше нуля'; return false; }
      if (amount > MANUAL_REPLENISH_MAX) { this.amount.error = `Не более ${MANUAL_REPLENISH_MAX.toLocaleString('ru-RU')} ₽ за одну операцию`; return false; }
      if (this.note.value.length > 500) { this.note.error = 'Не более 500 символов'; return false; }
      return true;
    }

    reset() {
      this.amount.reset();
      this.note.reset();
      this.resetForm();
    }
  }

  export interface UserLogsResponse {
    count: number;
    results: UserLogItem[];
  }

  export enum EmailStatus {
    VERIFIED = 'verified',
    CONFIRM = 'confirm',
    CHANGE = 'change'
  }

  export enum UserRole {
    user = 'Пользователь',
    admin = 'Администратор',
    moder = 'Модератор',
    senior_moder = 'Старший модератор'
  }

  export interface UserSocialAuth { provider: SocialProvider; }

  export enum SocialProvider {
    FACEBOOK = 'facebook',
    GOOGLE = 'google-oauth2',
    VK = 'vk-oauth2',
    OK = 'odnoklassniki-oauth2'
  }

  export enum SocialProviderRU {
    'facebook' = 'Facebook',
    'google-oauth2' = 'Google',
    'vk-oauth2' = 'Вконтакте',
    'odnoklassniki-oauth2' = 'Одноклассники'
  }
  
  export class SignInForm extends UI.Form {
    username: UI.FormField = new UI.FormField();
    password: UI.FormField = new UI.FormField();

    constructor() {
      super();
    }

    isValid(): boolean {
      if (!this.username.isValid()) return false;
      if (!this.password.isValid()) return false;
      return true;
    }

    reset() {
      this.username.reset();
      this.password.reset();
      this.resetForm();
    }
  }

  export class ModerForm extends UI.Form {
    user: Profile;
    userError: string = '';
    role: UI.FormField;
    permissions: UserPermissions;

    constructor(user?: Profile) {
      super();
      this.user = user ? user : null;
      this.role = new UI.FormField(user ? user.role : '');
      this.permissions = {
        is_auto_confirm_ip: user ? !!user.is_auto_confirm_ip : false,
        is_ability_to_edit_projects: user ? !!user.is_ability_to_edit_projects : false,
        is_ability_to_delete_projects: user ? !!user.is_ability_to_delete_projects : false,
        is_auto_review_edits: user ? !!user.is_auto_review_edits : false,
        is_ability_to_transfer: user ? !!user.is_ability_to_transfer : false,
        is_ability_to_delete_comments: user ? !!user.is_ability_to_delete_comments : false,
      };
    }

    isValid(): boolean {
      if (!this.user) {
        this.userError = 'Выберите пользователя'
        return false;
      }
      if (!this.role.isValid()) return false;
      return true;
    }

    resetErrors() {
      this.userError = '';
      this.role.error = '';
      this.error = '';
    }
  }

  export enum UserModerationAction {
    BAN = 'ban',
    UNBAN = 'unban',
  }

  export enum UserBanReason {
    obscene_language = 'obscene_language',
    fraud = 'fraud',
    spam = 'spam',
    other = 'other'
  }

  export interface UserLogItem {
    id: number;
    action: UserModerationAction;
    actor: User.Profile;
    reason: UserBanReason;
    details: string;
    timestamp: Date;
    user: number;
  }
}